/**
 * VendorContact — reusable "message this vendor" widget for
 * authenticated landlords. Renders as a card with a subject/body
 * form; on submit calls POST /api/vendors/:id/message which persists
 * the thread + forwards to the vendor's phone via Twilio SMS.
 *
 * When the caller isn't a landlord (unauth, or a tenant) we still
 * render but with a locked CTA that routes to /login/landlord — this
 * keeps the vendor page from looking empty to signed-out visitors.
 */
import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Lock, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/friendlyError";

export default function VendorContact({ vendorId, vendorName, vendorPhone, user }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  const role = (user?.role || "").toLowerCase();
  const canMessage = user && (role === "landlord" || role === "admin");

  const send = async () => {
    if (!body.trim() || body.trim().length < 2) {
      toast.error("Add a message first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/vendors/${encodeURIComponent(vendorId)}/message`, {
        subject: subject.trim() || undefined,
        body: body.trim(),
      });
      setLastSent(r.data?.message || null);
      setSubject("");
      setBody("");
      const smsFlag = r.data?.message?.sms_delivered;
      toast.success(smsFlag
        ? `Message sent — SMS delivered to ${vendorName || "vendor"}.`
        : "Message sent — vendor will see it in-app.");
    } catch (e) {
      toast.error(friendlyError(e, "Could not send message"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 p-5 rounded-2xl bg-white border border-slate-200 max-w-3xl" data-testid="vendor-contact-card">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-emerald-600" />
        <h3 className="font-display text-lg font-bold text-slate-900">Message {vendorName || "this vendor"}</h3>
      </div>
      {vendorPhone && canMessage && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <Phone className="w-3.5 h-3.5"/>
          A copy of your message goes straight to their phone via SMS.
        </div>
      )}

      {!canMessage ? (
        <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <Lock className="w-4 h-4 mt-0.5 text-slate-500 flex-none"/>
          <div className="text-sm text-slate-700">
            Sign in as a landlord to message vendors directly.{" "}
            <Link to="/login/landlord" className="font-semibold text-emerald-700 hover:underline" data-testid="vendor-contact-login">
              Sign in →
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)"
            maxLength={140}
            data-testid="vendor-contact-subject"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Tell them about the job — property, timing, budget."
            rows={4}
            maxLength={1500}
            data-testid="vendor-contact-body"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none resize-y"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 tabular-nums">{body.length}/1500</span>
            <button
              type="button"
              onClick={send}
              disabled={busy || !body.trim()}
              data-testid="vendor-contact-send"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold ${busy || !body.trim() ? "bg-slate-200 text-slate-500" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
            >
              <Send className="w-4 h-4"/>{busy ? "Sending…" : "Send message"}
            </button>
          </div>
          {lastSent && (
            <div className="mt-2 text-xs text-emerald-700 font-semibold" data-testid="vendor-contact-last">
              ✓ Message sent {lastSent.sms_delivered ? "(SMS delivered)" : "(SMS not attempted — no phone on file)"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
